import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon } from 'lucide-react';
import { WebsiteExperience } from '../components/website/WebsiteExperience';
import { AboutExperience } from '../components/website/AboutExperience';
import { EpkExperience } from '../components/website/EpkExperience';
import { useLiveTourDates } from '../hooks/useLiveTourDates';
import { getDraftWebsiteContent, websiteDraftStorageKey, websiteDraftUpdatedEvent, type WebsiteContent } from '../data/website';
type PreviewPage = 'home' | 'about' | 'epk';
function requestedPage(): PreviewPage {
  const page = new URLSearchParams(window.location.search).get('page');
  return page === 'about' || page === 'epk' ? page : 'home';
}

/**
 * The draft site, rendered inside the studio rather than in a new tab.
 *
 * Opening a new tab is unreliable while the project is still previewed on a
 * temporary address — the new tab can land on a different address with its own
 * empty storage, so none of the draft edits appear. Rendering here guarantees the
 * preview reads the exact draft you are editing.
 */
export function DraftPreview() {
  const [content, setContent] = useState<WebsiteContent>(() => getDraftWebsiteContent());
  const [page, setPage] = useState<PreviewPage>(requestedPage);
  const withLiveDates = useLiveTourDates(content);
  useEffect(() => {
    const refresh = () => setContent(getDraftWebsiteContent());
    const sync = (event: Event) => setContent((event as CustomEvent<WebsiteContent>).detail ?? getDraftWebsiteContent());
    const syncStorage = (event: StorageEvent) => {
      if (event.key === websiteDraftStorageKey) refresh();
    };
    window.addEventListener(websiteDraftUpdatedEvent, sync);
    window.addEventListener('storage', syncStorage);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(websiteDraftUpdatedEvent, sync);
      window.removeEventListener('storage', syncStorage);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  return <div className="-mx-5 -mt-7 -mb-28 sm:-mx-8 sm:-mt-10" data-id="element-1471" data-mp="26sfwhn1dbmda">
      <div className="sticky top-0 z-[70] flex flex-wrap items-center justify-between gap-3 bg-[#d97742] px-4 py-2.5 text-white sm:px-6" data-id="element-1472" data-mp="26sz3w9te6sd2">
        <Link to="/studio/website" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium underline-offset-2 hover:underline" data-id="element-1473" data-mp="2gi8z3yjj8r5e">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1474" data-mp="2ljpzzu20odlz" /> Back to Website Studio
        </Link>

        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium" data-id="element-1475" data-mp="2qevjynbuzi7f">
          <EyeIcon className="h-3.5 w-3.5" data-id="element-1476" data-mp="2uitfyp5w0cw9" /> Draft preview — fans still see the published version
        </span>

        <div className="flex items-center gap-1 rounded-full bg-white/15 p-0.5" data-id="element-1477" data-mp="2zfafsgmg8fp6">
          {(['home', 'about', 'epk'] as PreviewPage[]).map(option => <button key={option} type="button" onClick={() => setPage(option)} aria-pressed={page === option} className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors duration-150 ease-out ${page === option ? 'bg-white text-[#8f4519]' : 'text-white/85 hover:text-white'}`} data-id="element-1478" data-mp="152kcx9e3rw5n">
              {option === 'home' ? 'Home' : option === 'about' ? 'About' : 'Press kit'}
            </button>)}
        </div>
      </div>

      {page === 'home' && <WebsiteExperience content={withLiveDates} data-id="element-1479" data-mp="1bjvke6rybj7g" />}
      {page === 'about' && <AboutExperience content={content} data-id="element-1480" data-mp="1bhj7i811vz19" />}
      {page === 'epk' && <EpkExperience content={content} data-id="element-1481" data-mp="16j7trm7mx4dr" />}
    </div>;
}